import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import {
  FaSearch,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaHeart,
  FaRegHeart,
  FaImages,
  FaVideo,
  FaArrowRight,
} from "react-icons/fa";

import "./Gallery.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


function Gallery() {

  /* =====================================================
     STATE
  ===================================================== */

  const [galleryItems, setGalleryItems] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [activeCategory, setActiveCategory] =
    useState("All");

  const [search, setSearch] =
    useState("");

  const [selectedItem, setSelectedItem] =
    useState(null);

  const [favorites, setFavorites] =
    useState([]);


  /* =====================================================
     FETCH GALLERY
  ===================================================== */

  const fetchGallery = async () => {
    try {

      setLoading(true);

      setError("");

      const response =
        await axios.get(
          `${API_BASE_URL}/api/gallery`,
          {
            params: {
              limit: 100,
            },
          }
        );

      setGalleryItems(
        response.data?.data || []
      );

    } catch (error) {

      console.error(
        "Failed to load gallery:",
        error
      );

      setError(
        "We couldn't load the gallery. Please try again."
      );

      setGalleryItems([]);

    } finally {

      setLoading(false);

    }
  };


  useEffect(() => {

    fetchGallery();

  }, []);


  /* =====================================================
     CREATE FULL MEDIA URL
  ===================================================== */

  const getMediaUrl = (url) => {

    if (!url) {
      return "";
    }

    if (
      url.startsWith("http://") ||
      url.startsWith("https://")
    ) {
      return url;
    }

    return `${API_BASE_URL}${url}`;

  };


  /* =====================================================
     VIDEO URL CONVERSION
     
     Converts:
     
     https://www.youtube.com/watch?v=VIDEO_ID
     
     https://youtu.be/VIDEO_ID
     
     Into:
     
     https://www.youtube.com/embed/VIDEO_ID
  ===================================================== */

  const getVideoEmbedUrl = (url) => {

    if (!url) {
      return "";
    }

    try {

      // Already an embed URL
      if (url.includes("/embed/")) {
        return url;
      }


      // YouTube short URL
      if (
        url.includes("youtu.be/")
      ) {

        const videoId =
          url
            .split("youtu.be/")[1]
            ?.split("?")[0];

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        }

      }


      // Normal YouTube URL
      if (
        url.includes("youtube.com")
      ) {

        const urlObject =
          new URL(url);

        const videoId =
          urlObject.searchParams.get("v");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        }

      }


      // Vimeo
      if (
        url.includes("vimeo.com")
      ) {

        const videoId =
          url
            .split("/")
            .filter(Boolean)
            .pop();

        if (videoId) {
          return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
        }

      }


      // Otherwise return the URL
      return url;

    } catch (error) {

      console.error(
        "Invalid video URL:",
        error
      );

      return url;

    }

  };


  /* =====================================================
     DYNAMIC CATEGORIES
  ===================================================== */

  const categories = useMemo(() => {

    const uniqueCategories =
      [
        ...new Set(
          galleryItems
            .map(
              (item) =>
                item.category
            )
            .filter(Boolean)
        ),
      ];

    return [
      "All",
      ...uniqueCategories,
      "Videos",
    ];

  }, [galleryItems]);


  /* =====================================================
     FILTER GALLERY
  ===================================================== */

  const filteredItems = useMemo(() => {

    const searchTerm =
      search
        .toLowerCase()
        .trim();

    return galleryItems.filter(
      (item) => {

        /* CATEGORY */

        const matchesCategory =
          activeCategory === "All"
            ? true
            : activeCategory ===
              "Videos"
              ? item.type === "video"
              : item.category ===
              activeCategory;


        /* SEARCH */

        const matchesSearch =
          !searchTerm ||

          item.title
            ?.toLowerCase()
            .includes(searchTerm) ||

          item.location
            ?.toLowerCase()
            .includes(searchTerm) ||

          item.category
            ?.toLowerCase()
            .includes(searchTerm);


        return (
          matchesCategory &&
          matchesSearch
        );

      }
    );

  }, [
    galleryItems,
    activeCategory,
    search,
  ]);


  /* =====================================================
     SELECTED ITEM INDEX
  ===================================================== */

  const currentIndex =
    selectedItem
      ? filteredItems.findIndex(
        (item) =>
          item.id ===
          selectedItem.id
      )
      : -1;


  /* =====================================================
     OPEN ITEM
  ===================================================== */

  const openItem = (item) => {

    setSelectedItem(item);

    document.body.style.overflow =
      "hidden";

  };


  /* =====================================================
     CLOSE MODAL
  ===================================================== */

  const closeModal = () => {

    setSelectedItem(null);

    document.body.style.overflow =
      "";

  };


  /* =====================================================
     NEXT ITEM
  ===================================================== */

  const showNext = () => {

    if (
      !filteredItems.length ||
      currentIndex === -1
    ) {
      return;
    }

    const nextIndex =
      currentIndex ===
        filteredItems.length - 1
        ? 0
        : currentIndex + 1;

    setSelectedItem(
      filteredItems[nextIndex]
    );

  };


  /* =====================================================
     PREVIOUS ITEM
  ===================================================== */

  const showPrevious = () => {

    if (
      !filteredItems.length ||
      currentIndex === -1
    ) {
      return;
    }

    const previousIndex =
      currentIndex === 0
        ? filteredItems.length - 1
        : currentIndex - 1;

    setSelectedItem(
      filteredItems[
      previousIndex
      ]
    );

  };


  /* =====================================================
     FAVORITES
  ===================================================== */

  const toggleFavorite = (
    id,
    event
  ) => {

    event.stopPropagation();

    setFavorites(
      (previous) => {

        if (
          previous.includes(id)
        ) {
          return previous.filter(
            (itemId) =>
              itemId !== id
          );
        }

        return [
          ...previous,
          id,
        ];

      }
    );

  };


  /* =====================================================
     KEYBOARD NAVIGATION
  ===================================================== */

  useEffect(() => {

    const handleKeyDown =
      (event) => {

        if (!selectedItem) {
          return;
        }


        if (
          event.key === "Escape"
        ) {
          closeModal();
        }


        if (
          event.key ===
          "ArrowRight"
        ) {
          showNext();
        }


        if (
          event.key ===
          "ArrowLeft"
        ) {
          showPrevious();
        }

      };


    window.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  }, [
    selectedItem,
    currentIndex,
    filteredItems,
  ]);


  /* =====================================================
     CLEAN UP BODY SCROLL
  ===================================================== */

  useEffect(() => {

    return () => {

      document.body.style.overflow =
        "";

    };

  }, []);


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div className="gallery-page">


      {/* =================================================
          HERO
      ================================================= */}

      <section className="gallery-hero">

        <div className="gallery-hero-overlay"></div>


        <div className="gallery-hero-content">

          <div className="gallery-eyebrow">

            <FaImages />

            <span>
              PRIMEDALE TRAVEL MOMENTS
            </span>

          </div>


          <h1>

            Every Journey Has

            <span>
              A Story
            </span>

          </h1>


          <p>

            Explore unforgettable moments,
            breathtaking destinations,
            incredible wildlife and amazing
            experiences captured throughout
            our journeys.

          </p>


          <div className="gallery-stats">

            <div>

              <strong>
                {galleryItems.length}+
              </strong>

              <span>
                Travel Moments
              </span>

            </div>


            <div>

              <strong>
                {categories.length - 2}+
              </strong>

              <span>
                Experiences
              </span>

            </div>


            <div>

              <strong>
                100%
              </strong>

              <span>
                Unforgettable
              </span>

            </div>

          </div>

        </div>


        <div className="scroll-indicator">

          <span>
            EXPLORE GALLERY
          </span>

          <div className="scroll-line"></div>

        </div>

      </section>



      {/* =================================================
          MAIN GALLERY
      ================================================= */}

      <section className="gallery-section">

        <div className="gallery-container">


          {/* HEADER */}

          <div className="gallery-header ">

            <div>

              <span className="section-tag">

                EXPLORE THE WORLD

              </span>


              <h2 className="col">

                Captured

                <span>
                  Experiences
                </span>

              </h2>


              <p>

                From breathtaking safaris and
                relaxing beach escapes to
                unforgettable adventures,
                explore the world through
                Primedale Tours.

              </p>

            </div>


            <div className="gallery-count">

              <FaImages />

              <span>

                {filteredItems.length}

                {" "}

                {filteredItems.length === 1
                  ? "Experience"
                  : "Experiences"
                }

              </span>

            </div>

          </div>



          {/* SEARCH AND FILTERS */}

          <div className="gallery-controls">


            {/* SEARCH */}

            <div className="gallery-search">

              <FaSearch />


              <input
                type="text"
                placeholder="Search destinations, experiences..."
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
              />


              {search && (

                <button
                  type="button"
                  onClick={() =>
                    setSearch("")
                  }
                  aria-label="Clear search"
                >

                  <FaTimes />

                </button>

              )}

            </div>



            {/* FILTERS */}

            <div className="gallery-filters">

              {categories.map(
                (category) => (

                  <button
                    type="button"
                    key={category}
                    className={
                      activeCategory ===
                        category
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveCategory(
                        category
                      )
                    }
                  >

                    {category ===
                      "Videos" && (
                        <FaVideo />
                      )}

                    {category}

                  </button>

                )
              )}

            </div>

          </div>



          {/* =============================================
              LOADING
          ============================================== */}

          {loading && (

            <div className="gallery-loading">

              <div className="gallery-spinner"></div>

              <h3>

                Loading unforgettable
                experiences...

              </h3>

            </div>

          )}



          {/* =============================================
              ERROR
          ============================================== */}

          {!loading &&
            error && (

              <div className="gallery-empty">

                <FaImages />

                <h3>

                  Unable to load gallery

                </h3>

                <p>
                  {error}
                </p>

                <button
                  type="button"
                  onClick={
                    fetchGallery
                  }
                >

                  Try Again

                </button>

              </div>

            )}



          {/* =============================================
              GALLERY GRID
          ============================================== */}

          {!loading &&
            !error &&
            filteredItems.length > 0 && (

              <div className="gallery-grid">

                {filteredItems.map(
                  (item) => {

                    const mediaUrl =
                      item.type ===
                        "video"
                        ? getMediaUrl(
                          item.thumbnail_url
                        )
                        : getMediaUrl(
                          item.image_url
                        );


                    return (

                      <article
                        className={`gallery-card ${Number(
                          item.featured
                        ) === 1
                          ? "featured"
                          : ""
                          }`}
                        key={item.id}
                        onClick={() =>
                          openItem(item)
                        }
                      >


                        {/* IMAGE / THUMBNAIL */}

                        {mediaUrl ? (

                          <img
                            src={mediaUrl}
                            alt={
                              item.title ||
                              "Primedale Tours Experience"
                            }
                            loading="lazy"
                          />

                        ) : (

                          <div className="gallery-no-media">

                            <FaImages />

                          </div>

                        )}


                        <div className="gallery-card-overlay"></div>



                        {/* VIDEO PLAY BUTTON */}

                        {item.type ===
                          "video" && (

                            <div className="video-play">

                              <FaPlay />

                            </div>

                          )}



                        {/* FAVORITE BUTTON */}

                        <button
                          type="button"
                          className="favorite-button"
                          onClick={(event) =>
                            toggleFavorite(
                              item.id,
                              event
                            )
                          }
                          aria-label={
                            favorites.includes(
                              item.id
                            )
                              ? "Remove from favorites"
                              : "Add to favorites"
                          }
                        >

                          {favorites.includes(
                            item.id
                          )
                            ? <FaHeart />
                            : <FaRegHeart />
                          }

                        </button>



                        {/* CARD CONTENT */}

                        <div className="gallery-card-content">

                          {/* <span>

                            {item.category ||
                              "Travel"
                            }

                          </span> */}


                          <h3>

                            {item.title}

                          </h3>


                          <p>

                            {item.category ||
                              "Explore with Primedale Tours"
                            }

                          </p>

                        </div>

                      </article>

                    );

                  }
                )}

              </div>

            )}



          {/* =============================================
              EMPTY STATE
          ============================================== */}

          {!loading &&
            !error &&
            filteredItems.length === 0 && (

              <div className="gallery-empty">

                <FaImages />

                <h3>

                  No experiences found

                </h3>

                <p>

                  Try another category
                  or search term.

                </p>


                <button
                  type="button"
                  onClick={() => {

                    setSearch("");

                    setActiveCategory(
                      "All"
                    );

                  }}
                >

                  Reset Gallery

                </button>

              </div>

            )}

        </div>

      </section>



      {/* =================================================
          CTA
      ================================================= */}

      <section className="gallery-cta">

        <div className="gallery-cta-overlay"></div>


        <div className="gallery-cta-content">

          <span>

            YOUR NEXT ADVENTURE
            STARTS HERE

          </span>


          <h2>

            Ready To Create Your

            <strong>

              Own Story?

            </strong>

          </h2>


          <p>

            Let Primedale Tours help you
            discover extraordinary destinations
            and create memories that will
            last a lifetime.

          </p>


          <a href="/packages">

            Explore Our Packages

            <FaArrowRight />

          </a>

        </div>

      </section>



      {/* =================================================
          LIGHTBOX / VIDEO MODAL
      ================================================= */}

      {selectedItem && (

        <div
          className="gallery-modal"
          onClick={closeModal}
        >


          {/* CLOSE */}

          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            aria-label="Close gallery"
          >

            <FaTimes />

          </button>



          {/* PREVIOUS */}

          {filteredItems.length > 1 && (

            <button
              type="button"
              className="modal-nav modal-prev"
              onClick={(event) => {

                event.stopPropagation();

                showPrevious();

              }}
              aria-label="Previous item"
            >

              <FaChevronLeft />

            </button>

          )}



          {/* MODAL CONTENT */}

          <div
            className="modal-content"
            onClick={(event) =>
              event.stopPropagation()
            }
          >


            {/* VIDEO */}

            {selectedItem.type ===
              "video" ? (

              <div className="video-container">

                <iframe
                  src={
                    getVideoEmbedUrl(
                      selectedItem.video_url
                    )
                  }
                  title={
                    selectedItem.title
                  }
                  allow="
                    autoplay;
                    encrypted-media;
                    picture-in-picture
                  "
                  allowFullScreen
                />

              </div>

            ) : (


              /* IMAGE */

              <img
                src={
                  getMediaUrl(
                    selectedItem.image_url
                  )
                }
                alt={
                  selectedItem.title ||
                  "Gallery image"
                }
              />

            )}



            {/* MODAL INFORMATION */}

            <div className="modal-info">

              <div>

                <h2>

                  {selectedItem.title}

                </h2>

                <span>

                  {selectedItem.category ||
                    "Travel"
                  }

                </span>


                <p>

                  {selectedItem.location ||
                    "Primedale Tours"
                  }

                </p>

              </div>


              <div className="modal-counter">

                {currentIndex + 1}

                {" / "}

                {filteredItems.length}

              </div>

            </div>

          </div>



          {/* NEXT */}

          {filteredItems.length > 1 && (

            <button
              type="button"
              className="modal-nav modal-next"
              onClick={(event) => {

                event.stopPropagation();

                showNext();

              }}
              aria-label="Next item"
            >

              <FaChevronRight />

            </button>

          )}

        </div>

      )}

    </div>

  );
}


export default Gallery;